"use client"

import { useState, useEffect } from "react"
import { X, Target, Calendar, Users, Trophy } from "lucide-react"
import api from "../request" // Adjust the import based on your API setup

function ChallengeModal({ isOpen, onClose, onGoalSaved, goalToEdit = null, goalCount = 0 }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "steps",
    target_value: "",
    duration: "daily",
    start_date: "",
    end_date: "",
  })

  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [activeGroups, setActiveGroups] = useState([])

  useEffect(() => {
    if (goalToEdit) {
      setForm({ ...goalToEdit })
    } else {
      setForm({
        title: "",
        description: "",
        type: "steps",
        target_value: "",
        duration: "daily",
        start_date: "",
        end_date: "",
      })
    }
  }, [goalToEdit, isOpen])

  useEffect(() => {
    fetchActiveGroups()
  }, [])

  const fetchActiveGroups = async () => {
    try {
      const res = await api.get("/hotstepper/groups")
      setActiveGroups(res.data)
    } catch {
      setError({ message: "Failed to fetch active groups", type: "error" })
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async () => {
    setSaving(true)
    setError(null)

    try {
      const url = goalToEdit ? `/challenges/${goalToEdit.id}` : "/challenges"
      const method = goalToEdit ? "put" : "post"

      const response = await api[method](url, form)
      onGoalSaved(response.data)
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong.")
    } finally {
      setSaving(false)
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const disableSubmit = goalCount >= 90 && !goalToEdit // fixed to 3 goals limit
  // Disable submit if there are already 3 active goals and not editing an existing one

  // Don't render if modal is not open
  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="relative bg-primary text-white p-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Trophy size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{goalToEdit ? "Edit Challenge" : "Create New Challenge"}</h2>
              <p className="text-blue-100 text-sm">Set your fitness goals and track progress</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-all duration-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(70vh-200px)]">
          {disableSubmit && (
            <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                <p className="text-red-800 text-sm font-medium">
                  You can only have up to 90 active challenges at a time.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                <p className="text-red-800 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Target size={16} className="text-primary" />
                Challenge Title *
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter an inspiring challenge title"
                value={form.title}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                required
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Description</label>
              <textarea
                name="description"
                placeholder="Describe your challenge goals and motivation..."
                value={form.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white resize-none"
              />
            </div>

            {/* Challenge Type and Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Challenge Type</label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                >
                  <option value="steps">🚶 Steps</option>
                  <option value="distance">📏 Distance</option>
                  <option value="calories">🔥 Calories</option>
                  <option value="duration">⏱️ Duration</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">Duration</label>
                <select
                  name="duration"
                  value={form.duration}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                >
                  <option value="daily">📅 Daily</option>
                  <option value="weekly">📊 Weekly</option>
                  <option value="monthly">📈 Monthly</option>
                </select>
              </div>
            </div>

            {/* Group Selection */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Users size={16} className="text-primary" />
                Select Group
              </label>
              <select
                name="group_id"
                value={form.group_id}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
              >
                <option value="">Choose a group (optional)</option>
                {activeGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Target Value */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">Target Value *</label>
              <div className="relative">
                <input
                  type="number"
                  name="target_value"
                  placeholder="Enter your target goal"
                  value={form.target_value}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                  required
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                  {form.type === "steps"
                    ? "steps"
                    : form.type === "distance"
                      ? "km"
                      : form.type === "calories"
                        ? "cal"
                        : "min"}
                </div>
              </div>
            </div>

            {/* Date Range */}
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Calendar size={16} className="text-primary" />
                Challenge Period
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Start Date</label>
                  <input
                    type="date"
                    name="start_date"
                    value={form.start_date || ""}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">End Date</label>
                  <input
                    type="date"
                    name="end_date"
                    value={form.end_date || ""}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 bg-gray-50 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || disableSubmit || !form.title || !form.target_value}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-primary border border-transparent rounded-xl hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {saving ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </div>
            ) : goalToEdit ? (
              "Update Challenge"
            ) : (
              "Create Challenge"
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ChallengeModal
